import { useEffect, useState } from "react";

import {
  Callout,
  FormGroup,
  Icon,
  IconName,
  InputGroup,
} from "@blueprintjs/core";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import uniqueId from "lodash/uniqueId";
import Select from "react-select";

import { Box, Flex } from "@/components/layout/flexbox";
import { TASK_TYPES } from "@/components/pages_logged_in/program/components/constants";
import { ControlledTextarea } from "@/components/pages_logged_in/program/components/set-components/ControlledTextarea";
import {
  couldEditQuestionResponse,
  getItemStyle,
} from "@/components/pages_logged_in/program/components/set-components/Customize";
import { StyledStickyBox } from "@/components/pages_logged_in/program/components/set-components/StyledStickyBox";
import { Button } from "@/components/ui/button";
import { CARD_CLASSES, MIDTEXT_COLOR } from "@/css/constants";
import { cn } from "@/lib/utils";
import {
  ConfigureInput,
  CustomFormField,
  NextInputProps,
} from "@/types/programs";
import { ProgramFormField, QuestionField } from "@/types/surveys";

const reorder = (
  list: CustomFormField[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

interface TaskCardProps {
  question: CustomFormField;
  defaultVisible?: boolean;
  onEdit: (newQuestion: CustomFormField) => void;
  onDelete: (question: CustomFormField) => void;
  allowEditResponseType?: boolean;
  allowDelete?: boolean;
  error?: string;
}

function TaskCard({
  question,
  defaultVisible = false,
  onEdit,
  onDelete,
  allowEditResponseType = false,
  allowDelete = true,
  error = "",
}: TaskCardProps) {
  const [cardDetailsVisible, setCardDetailsVisible] = useState(defaultVisible);
  const [isOpen, setIsOpen] = useState(false);
  const isEditMode = !question?.id?.includes("new");

  const options = TASK_TYPES.map((type) => ({
    value: type.value,
    label: type.name,
  }));

  const inputProps = {
    placeholder: "Insert link",
    fill: true,
    large: true,
    leftIcon: "link" as IconName,
    disabled: false,
    onClick: (e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation(),
  };

  const taskType =
    TASK_TYPES.find((type) => type.value === question?.answerType) ||
    TASK_TYPES[0];

  useEffect(() => {
    if (question?.link) {
      setIsOpen(true);
    }
  }, [question]);

  return (
    <div className={cn(CARD_CLASSES, "flex-1 p-4")}>
      <div
        className="flex w-full items-start cursor-pointer"
        onClick={() => {
          setCardDetailsVisible(!cardDetailsVisible);
        }}
      >
        <div>
          <Icon icon="drag-handle-vertical" />
        </div>
        <div className="flex-1 mx-3 flex flex-col gap-4">
          <FormGroup className="m-0">
            <div className="w-38.75">
              <Select
                isDisabled={isEditMode && !allowEditResponseType}
                placeholder="Select an option"
                value={options.find(
                  (type) => type.value === question?.answerType,
                )}
                onChange={(selected) => {
                  if (selected) {
                    onEdit({
                      ...question,
                      answerType: selected.value as QuestionField["answerType"],
                    });
                  }
                }}
                defaultValue={options[0]}
                isSearchable={true}
                options={options}
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9,
                  }),
                  container: (provided) => ({
                    ...provided,
                    width: "100%",
                  }),
                }}
              />
            </div>
          </FormGroup>
          <FormGroup className="m-0">
            <ControlledTextarea
              value={question?.prompt}
              autoResize={true}
              onChange={(newValue) => {
                onEdit({ ...question, prompt: newValue });
              }}
              large={true}
              fill={true}
              placeholder={taskType?.helperText}
              style={{ fontSize: 16 }}
            />
          </FormGroup>

          <FormGroup className="m-0">
            {!isOpen && (
              <Box width={150}>
                <Button
                  intent="secondary"
                  className="h-9.5 px-4 py-2.5 !text-sm !font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                >
                  Include Link <Icon icon="link" className="text-subtext" />
                </Button>
              </Box>
            )}
            {isOpen && (
              <div className="flex items-center gap-3">
                <InputGroup
                  defaultValue={question?.link || ""}
                  onChange={(e) => {
                    onEdit({
                      ...question,
                      link: e.target.value,
                    });
                  }}
                  autoFocus={false}
                  {...inputProps}
                />
                <Button
                  intent="secondary"
                  variant="minimal"
                  className="h-9.5 px-4 py-2.5 !text-sm !font-medium"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    onEdit({ ...question, link: undefined });
                    setIsOpen(!isOpen);
                  }}
                >
                  <Icon className="text-subtext" icon="minus" />
                </Button>
              </div>
            )}
          </FormGroup>
          {error && (
            <Callout intent="danger" icon="warning-sign">
              {error}
            </Callout>
          )}
        </div>
        {allowDelete && (
          <Button
            intent="secondary"
            variant="minimal"
            className="h-9.5 px-4 py-2.5 !text-sm !font-medium"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(question);
              setIsOpen(!isOpen);
            }}
          >
            <Icon icon="cross" className="text-subtext" />
          </Button>
        )}
      </div>
    </div>
  );
}

interface DefineProps {
  onNext: (data: NextInputProps) => void;
  input: ConfigureInput;
  saving?: boolean;
}

export function Define({ onNext, input, saving = false }: DefineProps) {
  const [questions, setQuestions] = useState<CustomFormField[]>([]);
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});

  const onPressNext = (
    newQuestions: CustomFormField[],
    isSave = true,
    isNext = true,
  ) => {
    if (isSave) {
      const errors: Record<string, string> = {};
      newQuestions.forEach((q) => {
        if (!q.prompt.trim()) {
          const id = q.id ?? "";
          errors[id] = "Please provide a prompt";
        }
      });
      setInputErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
    }

    onNext({
      value: {
        formFields: newQuestions.map((q, index) => {
          return {
            ...q,
            order: index + 1,
            required: true,
          };
        }),
      },
      isSave,
      isNext,
    });
  };

  function handleDragEnd(result: DropResult) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    onPressNext(
      reorder(questions, result?.source?.index, result?.destination?.index),
      false,
      false,
    );
  }

  useEffect(() => {
    const uID = uniqueId();
    const newQuestion: CustomFormField = {
      id: `new-question-${uID}`,
      prompt: "",
      answerType: TASK_TYPES[0].value as ProgramFormField["answerType"],
      order: 1,
      required: true,
    };
    setQuestions(input?.formFields?.length ? input.formFields : [newQuestion]);
  }, [input?.formFields]);

  const hasQuestions = questions?.length > 0;

  const couldEditResponseType = couldEditQuestionResponse(input);

  return (
    <>
      <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
        <div>
          <div className="text-2xl font-semibold">Define action items</div>
          <div className="text-subtext mt-2.5">
            Create activities for participants to complete
          </div>
        </div>

        <Callout
          intent="primary"
          style={{ lineHeight: "28px" }}
          title="Select from five action item formats"
        >
          <Box fontSize={14} color={MIDTEXT_COLOR}>
            Participants complete each through a specific action:
          </Box>
          <Box fontSize={14} color={MIDTEXT_COLOR}>
            <ul style={{ marginLeft: 24, padding: 0 }}>
              <li>
                <b>Task:</b> Manually marked as complete
              </li>
              <li>
                <b>Short Answer:</b> Completed by providing a brief response
              </li>
              <li>
                <b>Long Answer:</b> Completed by giving a multi-line response
              </li>
              <li>
                <b>URL Response:</b> Completed by submitting a URL
              </li>
              <li>
                <b>File Upload:</b> Completed by uploading the requested file
              </li>
            </ul>
          </Box>
        </Callout>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                className="round-corners flex-wrap flex flex-col gap-4"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {questions.map((question, index) => {
                  const isSingleLastItem =
                    index === questions.length - 1 && questions.length === 1;
                  return (
                    <Draggable
                      key={question.id}
                      draggableId={question.id ?? ""}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Flex
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style,
                          )}
                        >
                          <TaskCard
                            allowEditResponseType={couldEditResponseType}
                            defaultVisible={false}
                            question={question}
                            onEdit={(newQuestion) => {
                              const id = newQuestion.id ?? "";
                              setInputErrors({
                                ...inputErrors,
                                [id]: "",
                              });
                              onPressNext(
                                questions.map((q) => {
                                  if (q.id === question.id) {
                                    return newQuestion;
                                  }
                                  return q;
                                }),
                                false,
                                false,
                              );
                            }}
                            onDelete={(question) => {
                              onPressNext(
                                questions.filter((q) => q.id !== question.id),
                                false,
                                false,
                              );
                            }}
                            error={inputErrors[question?.id ?? ""]}
                            allowDelete={!isSingleLastItem}
                          />
                        </Flex>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div>
          <Button
            intent="secondary"
            className="h-9.5 px-4 py-2.5 !text-sm !font-medium"
            onClick={() => {
              let newQuestions: CustomFormField[] = [];
              const uID = uniqueId();
              const newQuestion: CustomFormField = {
                id: `new-question-${uID}`,
                prompt: "",
                answerType: TASK_TYPES[0]
                  .value as ProgramFormField["answerType"],
                required: true,
                order: questions.length + 1,
              };
              newQuestions = [...questions, newQuestion];
              onPressNext(newQuestions, false, false);
            }}
          >
            Add Task <Icon icon="plus" className="text-subtext" />
          </Button>
        </div>
      </div>
      <StyledStickyBox>
        <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9 py-0">
          <div>
            <Button
              className="min-w-40 px-4 py-2.5"
              intent="primary"
              onClick={() => {
                onPressNext(questions);
              }}
              disabled={saving || !hasQuestions}
            >
              Continue
            </Button>
          </div>
        </div>
      </StyledStickyBox>
    </>
  );
}
